/**
 * Agent API - Server Actions 통합
 *
 * React Query와 Server Actions를 통합하여 사용합니다.
 * 이 파일의 모든 함수는 Server Actions를 직접 호출합니다.
 */

import * as agentServerActions from '@/app/actions/agents';
import * as systemPromptServerActions from '@/app/actions/system-prompts';
import { Agent } from '../types/types';
import {
  CreateAgentRequest,
  UpdateAgentRequest,
  AnalyzeUrlsRequest,
  AnalyzeUrlsResponse,
  UploadFileResponse,
  DeleteAgentParams,
  SystemPrompt,
  SystemPromptsDict,
  TranslateSystemPromptRequest,
  TranslateSystemPromptResponse,
} from '../types/types';

/**
 * 공개 에이전트 조회 (인증 불필요)
 * 슬러그 페이지, 박람회/전시회 키오스크 환경에서 사용
 *
 * 참고: /api/public/agents/{id}는 AgentPreset 형식을 반환하므로
 * _original 필드에서 Agent 데이터를 추출하여 반환
 */
export const fetchPublicAgent = async (agentId: string): Promise<Agent> => {
  const response = await fetch(`/api/public/agents/${agentId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  // AgentPreset 형식에서 _original (원본 Agent 데이터) 추출
  // _original이 있으면 사용, 없으면 데이터를 Agent 형식으로 변환
  if (data._original) {
    return data._original as Agent;
  }

  // fallback: 데이터를 Agent 형식으로 변환
  return {
    id: data.agent_id || agentId,
    name: data.contents?.ko?.title || '',
    description: data.contents?.ko?.description || '',
    config: {
      avatar_type: data.avatar || 'kikiz',
      page_style: data.page_style,
      theme: data.theme_config,
      intro: data.contents,
    },
    is_active: true,
  } as Agent;
};

/**
 * 모든 에이전트 조회
 */
export const fetchAgents = async (): Promise<Agent[]> => {
  return agentServerActions.getAgents();
};

/**
 * 특정 에이전트 조회
 */
export const fetchAgent = async (agentId: string): Promise<Agent> => {
  return agentServerActions.getAgent(agentId);
};

/**
 * 휴지통의 에이전트 조회
 */
export const fetchTrashedAgents = async (): Promise<Agent[]> => {
  return agentServerActions.getTrashedAgents();
};

/**
 * 새 에이전트 생성
 */
export const createAgent = async (data: CreateAgentRequest): Promise<Agent> => {
  return agentServerActions.createAgent(data as any);
};

/**
 * 에이전트 수정
 */
export const updateAgent = async (
  agentId: string,
  data: UpdateAgentRequest
): Promise<Agent> => {
  return agentServerActions.updateAgent(agentId, data);
};

/**
 * 에이전트 삭제 (휴지통으로 이동 또는 영구 삭제)
 */
export const deleteAgent = async (
  agentId: string,
  params?: DeleteAgentParams
): Promise<{ success: boolean }> => {
  return agentServerActions.deleteAgent(agentId, params?.permanent ?? false);
};

/**
 * 에이전트 복원
 */
export const restoreAgent = async (agentId: string): Promise<Agent> => {
  return agentServerActions.restoreAgent(agentId);
};

/**
 * URL 분석 및 Q&A 생성 (다국어 지원)
 */
export const analyzeUrls = async (
  agentId: string,
  data: AnalyzeUrlsRequest
): Promise<AnalyzeUrlsResponse> => {
  return agentServerActions.analyzeUrls(
    agentId,
    data.urls,
    data.use_llm ?? true,
    data.auto_translate ?? true,
    data.target_languages
  );
};

/**
 * 파일 업로드
 */
export const uploadFile = async (
  agentId: string,
  formData: FormData
): Promise<UploadFileResponse> => {
  return agentServerActions.uploadFile(agentId, formData);
};

// ============================================================================
// System Prompts API
// ============================================================================

/**
 * 에이전트의 모든 언어별 시스템 프롬프트 조회
 */
export const fetchSystemPrompts = async (
  agentId: string
): Promise<SystemPrompt[]> => {
  return systemPromptServerActions.getSystemPrompts(agentId);
};

/**
 * 에이전트의 언어별 시스템 프롬프트를 딕셔너리 형태로 조회
 */
export const fetchSystemPromptsDict = async (
  agentId: string
): Promise<SystemPromptsDict> => {
  return systemPromptServerActions.getSystemPromptsDict(agentId);
};

/**
 * 특정 언어의 시스템 프롬프트 조회
 */
export const fetchSystemPromptByLanguage = async (
  agentId: string,
  languageCode: string
): Promise<SystemPrompt> => {
  return systemPromptServerActions.getSystemPromptByLanguage(
    agentId,
    languageCode
  );
};

/**
 * 새로운 언어의 시스템 프롬프트 생성
 */
export const createSystemPrompt = async (
  agentId: string,
  languageCode: string,
  content: string
): Promise<SystemPrompt> => {
  return systemPromptServerActions.createSystemPrompt(
    agentId,
    languageCode,
    content
  );
};

/**
 * 기존 시스템 프롬프트 수정
 */
export const updateSystemPrompt = async (
  agentId: string,
  languageCode: string,
  content: string
): Promise<SystemPrompt> => {
  return systemPromptServerActions.updateSystemPrompt(
    agentId,
    languageCode,
    content
  );
};

/**
 * 여러 언어의 시스템 프롬프트를 한 번에 생성/수정 (Batch)
 */
export const createOrUpdateSystemPromptsBatch = async (
  agentId: string,
  prompts: Record<string, string>
): Promise<SystemPrompt[]> => {
  return systemPromptServerActions.createOrUpdateSystemPromptsBatch(
    agentId,
    prompts
  );
};

/**
 * 시스템 프롬프트 삭제
 */
export const deleteSystemPrompt = async (
  agentId: string,
  languageCode: string,
  permanent: boolean = false
): Promise<{ message: string; language_code: string }> => {
  return systemPromptServerActions.deleteSystemPrompt(
    agentId,
    languageCode,
    permanent
  );
};

/**
 * 시스템 프롬프트 자동 번역
 */
export const translateSystemPrompt = async (
  agentId: string,
  data: TranslateSystemPromptRequest
): Promise<TranslateSystemPromptResponse> => {
  return systemPromptServerActions.translateSystemPrompt(
    agentId,
    data.source_language_code,
    data.source_content,
    data.target_language_codes
  );
};
